import React, { useEffect, useRef, useState } from 'react';
import { StyledButton } from '../Exams/styled';
import { Box, CircularProgress } from '@mui/material';
import { SearchContainer } from '../AcademicTerms/styled';
import DropdownSelect from '../../components/DropdownSelect';
import { academicYears } from '../../constants/academicYears';
import { AcademicTerm } from '../AcademicTerms/types';
import { useAcademicTermsSearch } from '../AcademicTerms/config/useAcademicTermSearch';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import academicTermsApis from '../../apis/academicTerms';
import { SnackbarSeverityType } from '../../enums';
import examApis from '../../apis/exams';
import { ExamResponse } from '../Exams/types';
import { AssessmentDetails } from './types';
import AssessmentOverviewTable from './components/AssessmentOverviewTable';
import { UpdateExamEvaluationPayload } from '../../apis/exams';
import { structureAssessmentData } from './types/structureAssessmentData';
import { isEqual } from 'lodash';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { ButtonContainer } from '../Staff/styled';
import { PageWrapper, Title } from '../common/styled';

interface AcademicTermsPageProps {
  allClasses?: SchoolClassesConfig;
  school: string;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AssessmentOverviewPage: React.FC<AcademicTermsPageProps> = ({
  allClasses,
  school,
  onShowToast
}) => {
  const [academicTerms, setAcademicTerms] = useState<AcademicTerm[]>([]);
  const [exams, setExams] = useState<ExamResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedExamId, setSelectedExamId] = useState('');
  const [editableAssessmentList, setEditableAssessmentList] = useState<
    AssessmentDetails[]
  >([]);
  const [initialAssessmentList, setInitialAssessmentList] = useState<
    AssessmentDetails[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const {
    academicYear,
    selectedGrade,
    selectedDivision,
    gradeOption,
    divisionOption,
    setAcademicYear,
    setSelectedGrade,
    setSelectedDivision,
    setDivisionOptions
  } = useAcademicTermsSearch(allClasses);

  const [prevAcademicYear, setPrevAcademicYear] =
    useState<string>(academicYear);

  const [isWrapped, setIsWrapped] = useState(false);
  const containerRef = useRef<any>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const items = Array.from(container.children) as HTMLElement[];
      if (items.length === 0) return;
      console.log(items);

      const firstRowTop = items[0].offsetTop;

      const isWrapped = items.some((item) => item.offsetTop > firstRowTop + 25);

      setIsWrapped(isWrapped);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const fetchAcademicTerms = async (
    year: string,
    grade: string,
    division: string
  ) => {
    try {
      const response = await academicTermsApis.getAYTerms(school, {
        academicYear: year,
        grade,
        division
      });
      setAcademicTerms(Array.isArray(response) ? response : []);
    } catch (error: any) {
      setAcademicTerms([]);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to load academic terms.',
        SnackbarSeverityType.error
      );
    }
  };

  const fetchExams = async (
    year: string,
    grade: string,
    division: string,
    termId: string
  ) => {
    try {
      const response = await examApis.getExamsFor(
        grade,
        division,
        year,
        school,
        termId
      );
      setExams(Array.isArray(response) ? response : []);
    } catch (error: any) {
      setExams([]);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to load academic exams.',
        SnackbarSeverityType.error
      );
    }
  };

  const fetchAssessmentList = async () => {
    try {
      const response = await examApis.getMarksList(school, {
        grade: selectedGrade,
        division: selectedDivision,
        academicYear,
        termId: selectedTermId,
        examId: selectedExamId
      });
      const structuredData = structureAssessmentData(response as any);
      setEditableAssessmentList(structuredData);
      setInitialAssessmentList(structuredData);
    } catch (error: any) {
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to load academic overviews.',
        SnackbarSeverityType.error
      );
    }
  };

  const handleAcademicYearChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    const newAcademicYear = event.target.value as string;
    setAcademicYear(newAcademicYear);
    setSelectedTermId('');
    setSelectedExamId('');
    setAcademicTerms([]);
    setExams([]);
    setIsSearched(false);
  };

  const handleGradeSearchChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setSelectedGrade(event.target.value as string);
    setSelectedDivision('');
    setDivisionOptions([]);
    setAcademicTerms([]);
    setIsSearched(false);
  };

  useEffect(() => {
    if (prevAcademicYear !== undefined && prevAcademicYear !== academicYear) {
      setSelectedTermId('');
      setSelectedExamId('');
      setAcademicTerms([]);
      setExams([]);
    }

    if (academicYear && selectedGrade && selectedDivision) {
      fetchAcademicTerms(academicYear, selectedGrade, selectedDivision);
    }

    if (academicYear && selectedGrade && selectedDivision) {
      fetchExams(
        academicYear,
        selectedGrade,
        selectedDivision,
        selectedTermId || ''
      );
    }

    setPrevAcademicYear(academicYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear, selectedGrade, selectedDivision, selectedTermId]);

  const handleSearch = async () => {
    setIsSubmitting(true);
    setIsSearched(false);
    await fetchAssessmentList();
    setIsSubmitting(false);
    setIsSearched(true);
  };

  const handleToggleEnableEvaluation = (id: string) => {
    setEditableAssessmentList((prevList) => {
      const updatedList = prevList.map((item) =>
        item.id === id
          ? { ...item, enableEvaluation: !item.enableEvaluation }
          : item
      );
      setHasChanges(!isEqual(updatedList, initialAssessmentList));
      return updatedList;
    });
  };

  const handleToggleAllEvaluationEnable = () => {
    const isAllEvaluationEnabled = editableAssessmentList.every(
      (assessment) => assessment.enableEvaluation === true
    );
    setEditableAssessmentList((prevList) => {
      const updatedList = prevList.map((item) => ({
        ...item,
        enableEvaluation: isAllEvaluationEnabled ? false : true
      }));
      setHasChanges(!isEqual(updatedList, initialAssessmentList));
      return updatedList;
    });
  };

  const handleSaveRequest = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsConfirmDialogOpen(false);
    setIsSubmitting(true);
    try {
      const payload = editableAssessmentList.map((item) => {
        const { examId, subjectId } = JSON.parse(item.id);
        return {
          examId,
          subjectId,
          disableEvaluation: !item.enableEvaluation
        };
      });

      const updatePayload: UpdateExamEvaluationPayload = {
        examEvaluationDetails: payload
      };
      await examApis.updateExamEvaluations(school, updatePayload);
      onShowToast(
        'Assessment evaluations updated successfully!',
        SnackbarSeverityType.success
      );
      setHasChanges(false);
      setInitialAssessmentList(editableAssessmentList);
    } catch (error: any) {
      onShowToast(
        error?.errors?.[0]?.message ||
          'Failed to update assessment evaluations.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  const isSearchDisabled =
    !academicYear ||
    !selectedGrade ||
    !selectedDivision ||
    !selectedTermId ||
    isSubmitting;

  const showTable =
    academicYear &&
    selectedGrade &&
    selectedDivision &&
    selectedTermId &&
    isSearched;

  const academicTermOptions = academicTerms.map((term) => ({
    label: term.name,
    value: term.id
  }));

  const academicExamOptions = exams.map((exam) => ({
    label: exam.name,
    value: exam.id
  }));

  return (
    <Box>
      <Title>Assessment Overview</Title>
      <PageWrapper sx={{ minHeight: 'auto !important' }}>
        <SearchContainer ref={containerRef}>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            value={academicYear}
            options={academicYears}
            onChange={handleAcademicYearChange}
          />
          <DropdownSelect
            label='Grade'
            placeholder='Select Grade'
            name='grade'
            value={selectedGrade}
            options={gradeOption}
            onChange={handleGradeSearchChange}
            disabled={!academicYear}
          />
          <DropdownSelect
            label='Division'
            placeholder='Select Division'
            name='division'
            value={selectedDivision}
            options={divisionOption}
            onChange={(e) => {
              setSelectedDivision(e.target.value as string);
              setSelectedTermId('');
              setSelectedExamId('');
              setIsSearched(false);
            }}
            disabled={!selectedGrade}
          />
          <DropdownSelect
            label='Term'
            placeholder='Select Term'
            name='term'
            value={selectedTermId}
            options={academicTermOptions}
            onChange={(e) => {
              setSelectedTermId(e.target.value as string);
              setSelectedExamId('');
              setIsSearched(false);
            }}
            disabled={!selectedDivision}
          />
          <DropdownSelect
            label='Exam'
            placeholder='Select Exam'
            name='exam'
            value={selectedExamId}
            options={academicExamOptions}
            onChange={(e) => {
              setSelectedExamId(e.target.value as string);
            }}
            disabled={!selectedTermId}
          />
          <ButtonContainer isWrapped={isWrapped}>
            <StyledButton
              onClick={handleSearch}
              disabled={isSearchDisabled}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto'
                },
                marginTop: '24px'
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Search'
              )}
            </StyledButton>
          </ButtonContainer>
        </SearchContainer>
      </PageWrapper>
      {showTable && (
        <AssessmentOverviewTable
          assessmentOverviewData={editableAssessmentList}
          isLoading={isSubmitting}
          hasChanges={hasChanges}
          handleToggleEnableEvaluation={handleToggleEnableEvaluation}
          handleToggleAllEvaluationEnable={handleToggleAllEvaluationEnable}
          onFinalSaveCheckBoxChange={handleSaveRequest}
        />
      )}
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        message={`Are you sure you want to save these changes?`}
        disable={isSubmitting}
      />
    </Box>
  );
};

export default AssessmentOverviewPage;
